import { latestArticles, pillarArticles } from '@/mocks/home';

// Site-level constants.
// The real site origin is resolved from the VITE_SITE_URL environment variable.
export const SITE_URL =
  (import.meta.env.VITE_SITE_URL as string | undefined) ||
  (typeof window !== 'undefined' ? window.location.origin : '');
export const SITE_NAME = 'AllChinaBuy Spreadsheet';

// Favicon provided by the authoritative SEO data (embed_code). Also reused as
// the Organization/WebSite logo and social share image because it is the only
// authoritative image URL available.
export const SITE_IMAGE =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png';

const LANGS = ['en', 'fr', 'de', 'da'];

const articles = [...latestArticles, ...pillarArticles];

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogType: string;
  jsonLd: Record<string, unknown>[] | null;
}

function ld(type: string, data: Record<string, unknown>): Record<string, unknown> {
  return { '@context': 'https://schema.org', '@type': type, ...data };
}

function breadcrumb(items: { name: string; url: string }[]): Record<string, unknown> {
  return ld('BreadcrumbList', {
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  });
}

function webPage(title: string, url: string, description: string): Record<string, unknown> {
  return ld('WebPage', { name: title, url, description });
}

function organizationJson(): Record<string, unknown> {
  return ld('Organization', {
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    logo: SITE_IMAGE,
  });
}

function websiteJson(): Record<string, unknown> {
  return ld('WebSite', {
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    inLanguage: LANGS,
    publisher: { '@type': 'Organization', name: SITE_NAME, logo: SITE_IMAGE },
  });
}

const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: 'Is the AllChinaBuy spreadsheet free to use?',
    a: 'Yes, the AllChinaBuy spreadsheet is typically available as a free resource for shoppers looking to discover products and sellers more efficiently.',
  },
  {
    q: 'How often is the AllChinaBuy spreadsheet updated?',
    a: 'Update frequency varies, but most active spreadsheets are refreshed monthly or quarterly to reflect new products, price changes, and seller status updates.',
  },
  {
    q: 'Can I trust all sellers listed in the spreadsheet?',
    a: 'While the spreadsheet aims to include reliable sellers, we recommend always doing your own verification, checking recent reviews, and starting with smaller purchases when trying new sellers.',
  },
  {
    q: 'Does the spreadsheet include pricing information?',
    a: 'Most spreadsheets include estimated price ranges to help with budgeting, but actual prices may vary based on current promotions, shipping choices, and currency fluctuations.',
  },
  {
    q: 'What should I do if a product link is broken?',
    a: 'Broken links are common in dynamic spreadsheets. Try searching for the seller name or product category directly on the main platform, or check for an updated version of the spreadsheet.',
  },
];

function faqPageJson(): Record<string, unknown> {
  return ld('FAQPage', {
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  });
}

// Home / language home metadata (matches index.html shared fallback).
const HOME = {
  title: 'AllChinaBuy Spreadsheet 2026: Complete Guide & Best Resources',
  description:
    'Discover the AllChinaBuy Spreadsheet — your independent resource hub for products, sellers, QC guides, shipping tips, and buying resources. Updated regularly.',
  keywords:
    'AllChinaBuy, spreadsheet, sellers, products, QC guide, shipping, warehouse, review, buying guide',
};

const MAIN_PAGES: Record<
  string,
  { title: string; description: string; keywords: string }
> = {
  'allchinabuy-spreadsheet': {
    title: 'AllChinaBuy Spreadsheet Hub 2026: Products, Sellers & Categories',
    description:
      'Explore the AllChinaBuy spreadsheet hub to discover products, compare verified sellers, and browse 14 product categories with QC details, shipping tips, and buying resources.',
    keywords: 'AllChinaBuy spreadsheet, products, sellers, categories, QC, buying guide',
  },
  guides: {
    title: 'AllChinaBuy Shopping Guides 2026: Buying, QC, Shipping & More',
    description:
      'Read AllChinaBuy shopping guides covering first orders, QC photos, shipping comparisons, trusted sellers, and safety tips to shop smarter and safer.',
    keywords: 'AllChinaBuy guides, buying guide, QC, shipping, trusted sellers, safety',
  },
  review: {
    title: 'AllChinaBuy Review 2026: Features, Shipping, Warehouse & Safety',
    description:
      'Independent AllChinaBuy review covering how the platform works, warehouse and QC process, shipping options, safety, and key pros and cons for buyers.',
    keywords: 'AllChinaBuy review, features, shipping, warehouse, safety, pros and cons',
  },
  'shipping-cost': {
    title: 'AllChinaBuy Shipping Cost & Methods: Complete 2026 Guide',
    description:
      'Compare AllChinaBuy shipping methods and costs — economy line, standard air, DHL, FedEx, and EMS — with timelines and money-saving tips.',
    keywords: 'AllChinaBuy shipping, shipping cost, methods, EMS, DHL, FedEx',
  },
  safety: {
    title: 'AllChinaBuy Safety Guide 2026: How to Shop More Safely',
    description:
      'AllChinaBuy safety guide covering account security, payment protection, seller verification, and common risks so you can shop with confidence.',
    keywords: 'AllChinaBuy safety, safe shopping, payment protection, seller verification, risks',
  },
  blog: {
    title: 'AllChinaBuy Blog: Guides, Reviews & Buying Resources 2026',
    description:
      'Browse AllChinaBuy blog articles and pillar guides covering spreadsheets, QC, shipping, trusted sellers, safety, and buying resources.',
    keywords: 'AllChinaBuy blog, guides, reviews, buying resources, QC, shipping',
  },
};

const CATEGORY_PAGES: Record<
  string,
  { title: string; description: string; keywords: string }
> = {
  clothing: {
    title: 'AllChinaBuy Clothing Spreadsheet: Streetwear & Casual Wear',
    description:
      'Explore the AllChinaBuy clothing spreadsheet — streetwear, casual wear, and designer-inspired pieces from verified sellers with sizing guides and QC details.',
    keywords: 'AllChinaBuy clothing, spreadsheet, streetwear, casual wear, sellers',
  },
  shoes: {
    title: 'AllChinaBuy Shoes Spreadsheet: Sneakers, Boots & More',
    description:
      'Browse the AllChinaBuy shoes spreadsheet featuring sneakers, boots, sandals, and more from verified sellers with detailed QC information.',
    keywords: 'AllChinaBuy shoes, spreadsheet, sneakers, boots, QC, sellers',
  },
  fashion: {
    title: 'AllChinaBuy Fashion Spreadsheet: Curated Collections & Trends',
    description:
      'Discover trending fashion in the AllChinaBuy spreadsheet — curated collections, seasonal picks, and style guides to elevate your wardrobe.',
    keywords: 'AllChinaBuy fashion, spreadsheet, trends, collections, style guide',
  },
  electronics: {
    title: 'AllChinaBuy Electronics Spreadsheet: Gadgets & Tech',
    description:
      'Find electronics and gadgets in the AllChinaBuy product spreadsheet, including accessories, tech wearables, and everyday devices.',
    keywords: 'AllChinaBuy electronics, spreadsheet, gadgets, tech accessories',
  },
  accessories: {
    title: 'AllChinaBuy Accessories Spreadsheet: Bags, Belts & More',
    description:
      'Shop belts, bags, jewelry, watches, and more through the AllChinaBuy accessories spreadsheet with detailed seller ratings.',
    keywords: 'AllChinaBuy accessories, spreadsheet, bags, belts, jewelry, watches',
  },
  'trusted-sellers': {
    title: 'AllChinaBuy Trusted Sellers: Verified Ratings & Reviews',
    description:
      'Access the AllChinaBuy trusted seller spreadsheet with verified reviews, ratings, and product specialties to shop with confidence.',
    keywords: 'AllChinaBuy trusted sellers, verified sellers, ratings, reviews',
  },
};

function buildCanonical(normalizedPath: string): string {
  if (normalizedPath === '/') return `${SITE_URL}/`;
  return `${SITE_URL}${normalizedPath}/`;
}

export function resolveSeo(pathname: string): SeoData {
  const normalized = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  const segments = normalized.split('/').filter(Boolean);

  let rest = segments;
  if (segments.length && LANGS.includes(segments[0])) {
    rest = segments.slice(1);
  }

  const canonical = buildCanonical(normalized);

  // Home (any language)
  if (rest.length === 0) {
    return {
      title: HOME.title,
      description: HOME.description,
      keywords: HOME.keywords,
      canonical,
      ogType: 'website',
      jsonLd: [organizationJson(), websiteJson()],
    };
  }

  const first = rest[0];

  // Spreadsheet hub / category detail
  if (first === 'allchinabuy-spreadsheet') {
    if (rest.length === 1) {
      const meta = MAIN_PAGES['allchinabuy-spreadsheet'];
      return {
        title: meta.title,
        description: meta.description,
        keywords: meta.keywords,
        canonical,
        ogType: 'website',
        jsonLd: [
          webPage(meta.title, canonical, meta.description),
          breadcrumb([{ name: SITE_NAME, url: `${SITE_URL}/` }, { name: 'Spreadsheet Hub', url: canonical }]),
        ],
      };
    }
    const slug = rest[1];
    const meta = CATEGORY_PAGES[slug];
    if (meta) {
      return {
        title: meta.title,
        description: meta.description,
        keywords: meta.keywords,
        canonical,
        ogType: 'website',
        jsonLd: [
          ld('CollectionPage', { name: meta.title, url: canonical, description: meta.description }),
          breadcrumb([
            { name: SITE_NAME, url: `${SITE_URL}/` },
            { name: 'Spreadsheet Hub', url: `${SITE_URL}/${segments[0]}/allchinabuy-spreadsheet/` },
            { name: meta.title, url: canonical },
          ]),
        ],
      };
    }
  }

  // Blog listing / article
  if (first === 'blog') {
    if (rest.length === 1) {
      const meta = MAIN_PAGES.blog;
      return {
        title: meta.title,
        description: meta.description,
        keywords: meta.keywords,
        canonical,
        ogType: 'website',
        jsonLd: [
          ld('Blog', { name: meta.title, url: canonical, description: meta.description }),
          breadcrumb([{ name: SITE_NAME, url: `${SITE_URL}/` }, { name: 'Blog', url: canonical }]),
        ],
      };
    }
    const slug = rest[1];
    const article = articles.find((a) => a.slug === slug);
    if (article) {
      return {
        title: article.title,
        description: article.description,
        keywords: `${article.category}, AllChinaBuy, ${article.slug.replace(/-/g, ' ')}, guide, buying resources`,
        canonical,
        ogType: 'article',
        jsonLd: [
          ld('BlogPosting', {
            headline: article.title,
            description: article.description,
            url: canonical,
            datePublished: article.updated,
            dateModified: article.updated,
            author: { '@type': 'Organization', name: SITE_NAME },
            publisher: {
              '@type': 'Organization',
              name: SITE_NAME,
              logo: { '@type': 'ImageObject', url: SITE_IMAGE },
            },
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
          }),
          faqPageJson(),
          breadcrumb([
            { name: SITE_NAME, url: `${SITE_URL}/` },
            { name: 'Blog', url: `${SITE_URL}/${segments[0]}/blog/` },
            { name: article.title, url: canonical },
          ]),
        ],
      };
    }
  }

  // Other main pages (guides, review, shipping-cost, safety)
  const meta = MAIN_PAGES[first];
  if (meta) {
    return {
      title: meta.title,
      description: meta.description,
      keywords: meta.keywords,
      canonical,
      ogType: 'website',
      jsonLd: [
        webPage(meta.title, canonical, meta.description),
        breadcrumb([{ name: SITE_NAME, url: `${SITE_URL}/` }, { name: meta.title, url: canonical }]),
      ],
    };
  }

  // Fallback for any unknown route (e.g. 404) — keep a safe generic value.
  return {
    title: HOME.title,
    description: HOME.description,
    keywords: HOME.keywords,
    canonical,
    ogType: 'website',
    jsonLd: null,
  };
}